/**
 * Battle performance probe — inactive unless enabled via Electron --battle-perf,
 * localStorage `coda:battlePerf`, or REACT_APP_BATTLE_PERF=1.
 *
 * Records parse / handler / commit / paint timings for game WebSocket messages.
 */

export const BATTLE_PERF_STORAGE_KEY = 'coda:battlePerf';
export const RING_BUFFER_MAX = 200;

export const NETWORK_PROFILES = {
  none: { label: 'None', latency: 0, wsInboundDelayMs: 0, downloadThroughput: -1, uploadThroughput: -1 },
  good: { label: 'Good (~30ms)', latency: 30, wsInboundDelayMs: 0, downloadThroughput: 5_000_000 / 8, uploadThroughput: 2_000_000 / 8 },
  average: { label: 'Average (~100ms)', latency: 100, wsInboundDelayMs: 50, downloadThroughput: 1_500_000 / 8, uploadThroughput: 750_000 / 8 },
  poor: { label: 'Poor (~250ms)', latency: 250, wsInboundDelayMs: 150, downloadThroughput: 400_000 / 8, uploadThroughput: 200_000 / 8 },
};

/** Reference bitrates for estimated download time (independent of active probe network profile). */
export const DOWNLOAD_REFERENCE_PROFILES = {
  poor400Kbps: {
    label: 'Poor mobile (~400 Kbps)',
    bitsPerSecond: 400_000,
  },
  average1_5Mbps: {
    label: 'Average home (~1.5 Mbps)',
    bitsPerSecond: 1_500_000,
  },
  good5Mbps: {
    label: 'Good broadband (~5 Mbps)',
    bitsPerSecond: 5_000_000,
  },
};

let probeSingleton = null;
let longTaskObserver = null;
let runtimeBattlePerfEnabled = false;
let runtimeNetworkProfile = null;

export function applyBattlePerfRuntimeConfig(config) {
  if (typeof window === 'undefined') {
    return false;
  }
  if (!config || typeof config !== 'object' || config.enabled !== true) {
    return false;
  }
  runtimeBattlePerfEnabled = true;
  window.__CODA_BATTLE_PERF__ = true;
  if (config.networkProfile && NETWORK_PROFILES[config.networkProfile]) {
    runtimeNetworkProfile = config.networkProfile;
    window.__CODA_BATTLE_PERF_NETWORK__ = config.networkProfile;
  }
  return true;
}

/**
 * Synchronous bootstrap from preload bridge / main-process injection (before React paint).
 */
export function syncBootstrapFromInjectedConfig() {
  if (typeof window === 'undefined') {
    return false;
  }
  if (runtimeBattlePerfEnabled) {
    return true;
  }
  const injected = window.__CODA_BATTLE_PERF_CONFIG__;
  if (injected?.enabled === true) {
    applyBattlePerfRuntimeConfig(injected);
    console.log('[BattlePerf] Enabled via injected preload config:', injected);
    return true;
  }
  if (window.electron?.perf?.enabled === true) {
    applyBattlePerfRuntimeConfig({
      enabled: true,
      networkProfile: window.electron?.perf?.networkProfile ?? 'none',
    });
    console.log('[BattlePerf] Enabled via window.electron.perf bridge');
    return true;
  }
  if (window.__CODA_BATTLE_PERF__ === true) {
    runtimeBattlePerfEnabled = true;
    return true;
  }
  return false;
}

/**
 * Renderer bootstrap when preload argv missed CLI flags (packaged Windows).
 * @returns {Promise<boolean>}
 */
export async function bootstrapBattlePerfFromElectron() {
  if (typeof window === 'undefined') {
    return false;
  }
  if (syncBootstrapFromInjectedConfig() || isBattlePerfEnabled()) {
    return true;
  }
  if (!window.electron?.perf?.getConfig) {
    return false;
  }
  const config = await window.electron.perf.getConfig();
  if (!config || config.enabled !== true) {
    console.warn('[BattlePerf] Main process config did not enable probe:', config);
    return false;
  }
  applyBattlePerfRuntimeConfig(config);
  console.log('[BattlePerf] Enabled via main-process IPC config:', config);
  return true;
}

export function isBattlePerfEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }
  if (runtimeBattlePerfEnabled) {
    return true;
  }
  if (syncBootstrapFromInjectedConfig()) {
    return true;
  }
  try {
    if (localStorage.getItem(BATTLE_PERF_STORAGE_KEY) === '1') {
      return true;
    }
  } catch (_) {
    // localStorage unavailable
  }
  if (process.env.REACT_APP_BATTLE_PERF === '1') {
    return true;
  }
  return false;
}

export function getNetworkProfileName() {
  if (typeof window === 'undefined') {
    return 'none';
  }
  if (runtimeNetworkProfile && NETWORK_PROFILES[runtimeNetworkProfile]) {
    return runtimeNetworkProfile;
  }
  const fromElectron = window.electron?.perf?.networkProfile;
  if (fromElectron && NETWORK_PROFILES[fromElectron]) {
    return fromElectron;
  }
  try {
    const stored = localStorage.getItem('coda:battlePerfNetworkProfile');
    if (stored && NETWORK_PROFILES[stored]) {
      return stored;
    }
  } catch (_) {
    // ignore
  }
  return 'none';
}

export function getWsInboundDelayMs() {
  const profile = NETWORK_PROFILES[getNetworkProfileName()];
  if (!profile) {
    return 0;
  }
  return profile.wsInboundDelayMs || 0;
}

export function percentile(values, p) {
  if (!values.length) {
    return 0;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, Math.min(idx, sorted.length - 1))];
}

function summarizeField(events, field) {
  const values = events.map((e) => e[field]).filter((v) => typeof v === 'number' && v >= 0);
  if (!values.length) {
    return { count: 0, p50: 0, p95: 0, max: 0 };
  }
  return {
    count: values.length,
    p50: Math.round(percentile(values, 50) * 100) / 100,
    p95: Math.round(percentile(values, 95) * 100) / 100,
    max: Math.round(Math.max(...values) * 100) / 100,
  };
}

/**
 * Estimated time to download a payload at a fixed reference bitrate (serialization only, no RTT).
 * @param {number} payloadBytes
 * @param {number} bitsPerSecond
 * @returns {number} milliseconds
 */
export function estimateDownloadMs(payloadBytes, bitsPerSecond) {
  if (typeof payloadBytes !== 'number' || payloadBytes < 0) {
    throw new Error('estimateDownloadMs: payloadBytes must be a non-negative number');
  }
  if (typeof bitsPerSecond !== 'number' || bitsPerSecond <= 0) {
    throw new Error('estimateDownloadMs: bitsPerSecond must be a positive number');
  }
  return Math.round(((payloadBytes * 8) / bitsPerSecond) * 1000 * 100) / 100;
}

function formatBytesForSummary(bytes) {
  if (bytes >= 1024 * 1024) {
    return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

function formatDurationMs(ms) {
  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 120) {
    return `${Math.round(seconds * 10) / 10} s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remSec = Math.round(seconds % 60);
  return `${minutes} min ${remSec} s`;
}

/**
 * @param {{ p50: number, p95: number, max: number }} payloadSummary
 */
export function buildEstimatedDownloadReport(payloadSummary) {
  if (!payloadSummary || typeof payloadSummary !== 'object') {
    throw new Error('buildEstimatedDownloadReport: payloadSummary is required');
  }

  const byProfile = {};
  for (const [profileId, profile] of Object.entries(DOWNLOAD_REFERENCE_PROFILES)) {
    const p50Ms = estimateDownloadMs(payloadSummary.p50, profile.bitsPerSecond);
    const p95Ms = estimateDownloadMs(payloadSummary.p95, profile.bitsPerSecond);
    const maxMs = estimateDownloadMs(payloadSummary.max, profile.bitsPerSecond);
    byProfile[profileId] = {
      label: profile.label,
      bitsPerSecond: profile.bitsPerSecond,
      fromPayloadBytes: {
        p50Bytes: payloadSummary.p50,
        p95Bytes: payloadSummary.p95,
        maxBytes: payloadSummary.max,
      },
      downloadMs: { p50: p50Ms, p95: p95Ms, max: maxMs },
      downloadSec: {
        p50: Math.round((p50Ms / 1000) * 100) / 100,
        p95: Math.round((p95Ms / 1000) * 100) / 100,
        max: Math.round((maxMs / 1000) * 100) / 100,
      },
    };
  }

  return {
    note: 'Download time is payload size / bitrate only (no RTT, TLS, or parse). Use to compare network impact on slow connections.',
    profiles: byProfile,
  };
}

function buildExactDuplicateKey(event) {
  return `${event.type}|${event.stateVersion}|${event.payloadBytes}`;
}

function inferDuplicateLikelyCause(classification, gapMs) {
  if (classification === 'exact_duplicate') {
    if (gapMs.length === 0) {
      return 'Same type, stateVersion, and payload size appeared more than once.';
    }
    const maxGap = Math.max(...gapMs);
    const minGap = Math.min(...gapMs);
    if (maxGap < 200) {
      return 'Bursts within 200 ms — likely double broadcast from server or duplicate WS handler invocation on the client.';
    }
    if (maxGap > 2000) {
      return 'Repeated minutes apart — possible reconnect resync or state refresh sending the same version again.';
    }
    return 'Repeated within 200 ms–2 s — may be server double-send, client handler registered twice, or rapid retry.';
  }
  if (classification === 'same_state_version_burst') {
    return 'Multiple message types at the same stateVersion in a short window — often normal (e.g. game-update then turn-played), but still extra handler work.';
  }
  if (classification === 'same_state_version_same_type_different_payload') {
    return 'Same type and stateVersion but different payload sizes — investigate message ordering or partial state races.';
  }
  return 'See event ids and gaps for details.';
}

/**
 * @param {Array<{ id: number, ts: number, type: string, stateVersion: number|null, payloadBytes: number }>} events
 */
export function analyzeDuplicateWsEvents(events) {
  if (!Array.isArray(events)) {
    throw new Error('analyzeDuplicateWsEvents: events must be an array');
  }

  const exactGroups = new Map();
  const stateVersionBuckets = new Map();

  for (const event of events) {
    const exactKey = buildExactDuplicateKey(event);
    if (!exactGroups.has(exactKey)) {
      exactGroups.set(exactKey, []);
    }
    exactGroups.get(exactKey).push(event);

    const versionKey = String(event.stateVersion);
    if (!stateVersionBuckets.has(versionKey)) {
      stateVersionBuckets.set(versionKey, []);
    }
    stateVersionBuckets.get(versionKey).push(event);
  }

  const groups = [];

  for (const [dedupeKey, groupEvents] of exactGroups) {
    if (groupEvents.length < 2) {
      continue;
    }
    const sorted = [...groupEvents].sort((a, b) => a.ts - b.ts);
    const gapMs = [];
    for (let i = 1; i < sorted.length; i += 1) {
      gapMs.push(sorted[i].ts - sorted[i - 1].ts);
    }
    const [type, stateVersionRaw, payloadBytesRaw] = dedupeKey.split('|');
    groups.push({
      classification: 'exact_duplicate',
      dedupeKey,
      type,
      stateVersion: stateVersionRaw === 'null' ? null : Number(stateVersionRaw),
      payloadBytes: Number(payloadBytesRaw),
      occurrenceCount: sorted.length,
      redundantEventCount: sorted.length - 1,
      eventIds: sorted.map((e) => e.id),
      timestamps: sorted.map((e) => e.ts),
      gapMs,
      likelyCause: inferDuplicateLikelyCause('exact_duplicate', gapMs),
    });
  }

  for (const [stateVersionRaw, bucketEvents] of stateVersionBuckets) {
    if (bucketEvents.length < 2) {
      continue;
    }
    const sorted = [...bucketEvents].sort((a, b) => a.ts - b.ts);
    const windowMs = 500;
    for (let i = 0; i < sorted.length; i += 1) {
      const cluster = [sorted[i]];
      for (let j = i + 1; j < sorted.length; j += 1) {
        if (sorted[j].ts - sorted[i].ts <= windowMs) {
          cluster.push(sorted[j]);
        } else {
          break;
        }
      }
      if (cluster.length < 2) {
        continue;
      }
      const types = [...new Set(cluster.map((e) => e.type))];
      const payloadSizes = [...new Set(cluster.map((e) => e.payloadBytes))];
      const exactKey = buildExactDuplicateKey(cluster[0]);
      const alreadyExact = groups.some(
        (g) => g.classification === 'exact_duplicate' && g.dedupeKey === exactKey && g.occurrenceCount === cluster.length
      );
      if (alreadyExact) {
        continue;
      }
      if (types.length === 1 && payloadSizes.length > 1) {
        groups.push({
          classification: 'same_state_version_same_type_different_payload',
          dedupeKey: `version:${stateVersionRaw}|type:${types[0]}`,
          type: types[0],
          stateVersion: stateVersionRaw === 'null' ? null : Number(stateVersionRaw),
          payloadBytes: null,
          occurrenceCount: cluster.length,
          redundantEventCount: cluster.length - 1,
          eventIds: cluster.map((e) => e.id),
          timestamps: cluster.map((e) => e.ts),
          gapMs: cluster.slice(1).map((e, idx) => e.ts - cluster[idx].ts),
          payloadBytesSeen: payloadSizes,
          typesSeen: types,
          likelyCause: inferDuplicateLikelyCause('same_state_version_same_type_different_payload', []),
        });
      } else if (types.length > 1) {
        const clusterKey = cluster.map((e) => e.id).join(',');
        const alreadyBurst = groups.some(
          (g) => g.classification === 'same_state_version_burst' && g.eventIds.join(',') === clusterKey
        );
        if (!alreadyBurst) {
          groups.push({
            classification: 'same_state_version_burst',
            dedupeKey: `version:${stateVersionRaw}|burst:${cluster[0].id}-${cluster[cluster.length - 1].id}`,
            type: types.join(' + '),
            stateVersion: stateVersionRaw === 'null' ? null : Number(stateVersionRaw),
            payloadBytes: null,
            occurrenceCount: cluster.length,
            redundantEventCount: cluster.length - 1,
            eventIds: cluster.map((e) => e.id),
            timestamps: cluster.map((e) => e.ts),
            gapMs: cluster.slice(1).map((e, idx) => e.ts - cluster[idx].ts),
            typesSeen: types,
            likelyCause: inferDuplicateLikelyCause('same_state_version_burst', []),
          });
        }
      }
    }
  }

  groups.sort((a, b) => b.redundantEventCount - a.redundantEventCount);

  const exactRedundant = groups
    .filter((g) => g.classification === 'exact_duplicate')
    .reduce((sum, g) => sum + g.redundantEventCount, 0);

  const totalRedundantExact = exactRedundant;
  const redundantPercent = events.length > 0
    ? Math.round((totalRedundantExact / events.length) * 1000) / 10
    : 0;

  const byClassification = {};
  for (const group of groups) {
    byClassification[group.classification] = (byClassification[group.classification] || 0) + 1;
  }

  return {
    totalEvents: events.length,
    exactDuplicateGroupCount: groups.filter((g) => g.classification === 'exact_duplicate').length,
    exactRedundantEventCount: totalRedundantExact,
    exactRedundantPercent: redundantPercent,
    burstGroupCount: groups.filter((g) => g.classification !== 'exact_duplicate').length,
    byClassification,
    groups,
    interpretation:
      totalRedundantExact === 0
        ? 'No exact duplicate WS messages detected (same type, stateVersion, and payload size).'
        : `${totalRedundantExact} redundant event(s) (${redundantPercent}% of total) are exact duplicates — prime targets for client coalescing or server broadcast dedupe.`,
  };
}

function buildBaselineMetrics(events, probe) {
  const withPayload = events.filter((e) => e.payloadBytes > 0);
  const first = events[0] || null;
  const last = events[events.length - 1] || null;
  const stateVersions = events
    .map((e) => e.stateVersion)
    .filter((v) => typeof v === 'number');
  const actionLogLengths = events
    .map((e) => e.actionLogLength)
    .filter((v) => typeof v === 'number');

  const typeCounts = {};
  for (const event of events) {
    typeCounts[event.type] = (typeCounts[event.type] || 0) + 1;
  }

  const largestPayload = withPayload.reduce(
    (best, e) => (!best || e.payloadBytes > best.payloadBytes ? e : best),
    null
  );
  const slowestHandler = events.reduce(
    (best, e) => (!best || (e.handlerMs || 0) > (best.handlerMs || 0) ? e : best),
    null
  );

  return {
    sessionDurationMs: Date.now() - probe.sessionStartedAt,
    messageCount: probe.messageCount,
    eventCount: events.length,
    stateVersionFirst: first?.stateVersion ?? null,
    stateVersionLast: last?.stateVersion ?? null,
    stateVersionMin: stateVersions.length ? Math.min(...stateVersions) : null,
    stateVersionMax: stateVersions.length ? Math.max(...stateVersions) : null,
    actionLogLengthFirst: first?.actionLogLength ?? null,
    actionLogLengthLast: last?.actionLogLength ?? null,
    actionLogLengthAtPeakPayload: largestPayload?.actionLogLength ?? null,
    payloadBytesFirst: first?.payloadBytes ?? null,
    payloadBytesLast: last?.payloadBytes ?? null,
    typeCounts,
    largestPayloadEvent: largestPayload
      ? {
          id: largestPayload.id,
          type: largestPayload.type,
          stateVersion: largestPayload.stateVersion,
          payloadBytes: largestPayload.payloadBytes,
          actionLogLength: largestPayload.actionLogLength,
        }
      : null,
    slowestHandlerEvent: slowestHandler
      ? {
          id: slowestHandler.id,
          type: slowestHandler.type,
          handlerMs: slowestHandler.handlerMs,
          payloadBytes: slowestHandler.payloadBytes,
        }
      : null,
  };
}

function buildWrittenSummary({
  baselineMetrics,
  summary,
  estimatedDownload,
  duplicateAnalysis,
  networkProfile,
  wsInboundDelayMs,
  gameId,
}) {
  const lines = [];
  const poor = estimatedDownload.profiles.poor400Kbps;
  const average = estimatedDownload.profiles.average1_5Mbps;

  lines.push(
    `Session: ${formatDurationMs(baselineMetrics.sessionDurationMs)}, ${baselineMetrics.eventCount} WS events recorded, stateVersion ${baselineMetrics.stateVersionFirst} → ${baselineMetrics.stateVersionLast}.`
  );

  if (gameId) {
    lines.push(`Game ID: ${gameId}.`);
  }

  lines.push(
    `Active probe network profile: ${networkProfile}${wsInboundDelayMs > 0 ? ` (synthetic +${wsInboundDelayMs} ms WS inbound delay)` : ''}.`
  );

  lines.push(
    `Payload size: first ${formatBytesForSummary(baselineMetrics.payloadBytesFirst || 0)}, last ${formatBytesForSummary(baselineMetrics.payloadBytesLast || 0)}, p95 ${formatBytesForSummary(summary.payloadBytes.p95)} (peak ${formatBytesForSummary(summary.payloadBytes.max)}).`
  );

  if (baselineMetrics.largestPayloadEvent) {
    const peak = baselineMetrics.largestPayloadEvent;
    lines.push(
      `Largest message: ${peak.type} at stateVersion ${peak.stateVersion}, ${formatBytesForSummary(peak.payloadBytes)}, actionLog length ${peak.actionLogLength}.`
    );
  }

  lines.push(
    `Estimated download time from payload p95 (${formatBytesForSummary(summary.payloadBytes.p95)}): ${poor.downloadSec.p95} s on ${poor.label}, ${average.downloadSec.p95} s on ${average.label}.`
  );

  lines.push(
    `Estimated download time from peak payload (${formatBytesForSummary(summary.payloadBytes.max)}): ${poor.downloadSec.max} s on ${poor.label}, ${average.downloadSec.max} s on ${average.label}.`
  );

  lines.push(
    `Client processing (this device): parse p95 ${summary.parseMs.p95} ms, handler p95 ${summary.handlerMs.p95} ms, paint p95 ${summary.paintMs.p95} ms, long-task max ${summary.longTaskMs.max} ms.`
  );

  lines.push(duplicateAnalysis.interpretation);

  if (duplicateAnalysis.exactDuplicateGroupCount > 0) {
    const top = duplicateAnalysis.groups
      .filter((g) => g.classification === 'exact_duplicate')
      .slice(0, 3)
      .map((g) => `${g.occurrenceCount}× ${g.type} @ v${g.stateVersion} (${formatBytesForSummary(g.payloadBytes)})`)
      .join('; ');
    lines.push(`Top exact duplicate groups: ${top}.`);
  }

  if (baselineMetrics.actionLogLengthFirst != null && baselineMetrics.actionLogLengthAtPeakPayload != null) {
    lines.push(
      `Action log growth: ${baselineMetrics.actionLogLengthFirst} entries at session start → ${baselineMetrics.actionLogLengthAtPeakPayload} at peak payload (last event: ${baselineMetrics.actionLogLengthLast}).`
    );
  }

  const topTypes = Object.entries(baselineMetrics.typeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([type, count]) => `${type} (${count})`)
    .join(', ');
  lines.push(`Message mix: ${topTypes}.`);

  return lines;
}

class BattlePerfProbe {
  constructor() {
    this.events = [];
    this.longTasks = [];
    this.sessionStartedAt = Date.now();
    this.pendingHandlerEndAt = null;
    this.messageCount = 0;
    this.listeners = new Set();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    for (const listener of this.listeners) {
      try {
        listener(this.getSnapshot());
      } catch (err) {
        console.error('[BattlePerf] listener error:', err);
      }
    }
  }

  pushEvent(event) {
    this.events.push(event);
    if (this.events.length > RING_BUFFER_MAX) {
      this.events.shift();
    }
    this.notify();
  }

  recordLongTask(durationMs) {
    this.longTasks.push({ ts: Date.now(), durationMs });
    if (this.longTasks.length > 50) {
      this.longTasks.shift();
    }
  }

  beginInboundMessage(rawData, wireByteLength) {
    // Phase 1.3: rawData may already be the expanded (decompressed) card-def payload. When the
    // transport layer decompressed it, wireByteLength carries the true on-the-wire (compressed)
    // size so the HUD reports the actual network payload, not the reconstructed one.
    const expandedBytes = typeof rawData === 'string' ? rawData.length : 0;
    const payloadBytes = typeof wireByteLength === 'number' ? wireByteLength : expandedBytes;
    return {
      id: ++this.messageCount,
      receivedAt: performance.now(),
      payloadBytes,
      parseStart: performance.now(),
    };
  }

  recordParsed(ctx, msg) {
    ctx.parseMs = performance.now() - ctx.parseStart;
    ctx.type = msg?.type || 'unknown';
    ctx.stateVersion = msg?.gameState?.stateVersion ?? msg?.gameStateMeta?.stateVersion ?? null;
    ctx.actionLogLength = Array.isArray(msg?.gameState?.actionLog)
      ? msg.gameState.actionLog.length
      : (Array.isArray(msg?.actionLogDelta) ? msg.actionLogDelta.length : 0);
    ctx.handlerStart = performance.now();
    return ctx;
  }

  recordHandlerComplete(ctx) {
    if (!ctx.handlerStart) {
      return;
    }
    ctx.handlerMs = performance.now() - ctx.handlerStart;
    ctx.handlerEndAt = performance.now();
    this.pendingHandlerEndAt = ctx.handlerEndAt;
    this.pushEvent({
      ts: Date.now(),
      id: ctx.id,
      type: ctx.type,
      stateVersion: ctx.stateVersion,
      payloadBytes: ctx.payloadBytes,
      actionLogLength: ctx.actionLogLength,
      parseMs: Math.round(ctx.parseMs * 100) / 100,
      handlerMs: Math.round(ctx.handlerMs * 100) / 100,
      commitMs: null,
      paintMs: null,
    });
  }

  recordCommit(commitMs) {
    const last = this.events[this.events.length - 1];
    if (!last || last.commitMs != null) {
      return;
    }
    last.commitMs = Math.round(commitMs * 100) / 100;
    this.notify();
  }

  recordPaint(paintMs) {
    const last = this.events[this.events.length - 1];
    if (!last || last.paintMs != null) {
      return;
    }
    last.paintMs = Math.round(paintMs * 100) / 100;
    this.notify();
  }

  getSnapshot() {
    return {
      sessionStartedAt: this.sessionStartedAt,
      messageCount: this.messageCount,
      eventCount: this.events.length,
      lastEvent: this.events[this.events.length - 1] || null,
      summary: this.getSummary(),
    };
  }

  getSummary() {
    const withGameState = this.events.filter((e) => e.payloadBytes > 0);
    return {
      parseMs: summarizeField(withGameState, 'parseMs'),
      handlerMs: summarizeField(withGameState, 'handlerMs'),
      commitMs: summarizeField(this.events, 'commitMs'),
      paintMs: summarizeField(this.events, 'paintMs'),
      payloadBytes: summarizeField(withGameState, 'payloadBytes'),
      longTaskMs: summarizeField(this.longTasks, 'durationMs'),
    };
  }

  buildExportReport(extra = {}) {
    const events = this.events.slice();
    const summary = this.getSummary();
    const estimatedDownload = buildEstimatedDownloadReport(summary.payloadBytes);
    const duplicateAnalysis = analyzeDuplicateWsEvents(events);
    const baselineMetrics = buildBaselineMetrics(events, this);
    const networkProfile = getNetworkProfileName();
    const wsInboundDelayMs = getWsInboundDelayMs();
    const gameId = extra.gameId ?? null;

    const writtenSummary = buildWrittenSummary({
      baselineMetrics,
      summary,
      estimatedDownload,
      duplicateAnalysis,
      networkProfile,
      wsInboundDelayMs,
      gameId,
    });

    return {
      exportedAt: new Date().toISOString(),
      enabled: true,
      networkProfile,
      wsInboundDelayMs,
      isElectron: typeof window !== 'undefined' && (window.location.protocol === 'file:' || window.electron != null),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      sessionDurationMs: Date.now() - this.sessionStartedAt,
      writtenSummary,
      baselineMetrics,
      estimatedDownload,
      duplicateAnalysis,
      summary,
      longTasks: this.longTasks.slice(-20),
      events,
      ...extra,
    };
  }
}

export function resetBattlePerfRuntimeStateForTests() {
  runtimeBattlePerfEnabled = false;
  runtimeNetworkProfile = null;
  probeSingleton = null;
}

export function getBattlePerfProbe() {
  if (!isBattlePerfEnabled()) {
    return null;
  }
  if (!probeSingleton) {
    probeSingleton = new BattlePerfProbe();
    installBattlePerfGlobalExport();
  }
  return probeSingleton;
}

export function startLongTaskObserver() {
  if (!isBattlePerfEnabled() || longTaskObserver || typeof PerformanceObserver === 'undefined') {
    return;
  }
  try {
    longTaskObserver = new PerformanceObserver((list) => {
      const probe = getBattlePerfProbe();
      if (!probe) {
        return;
      }
      for (const entry of list.getEntries()) {
        probe.recordLongTask(entry.duration);
      }
    });
    longTaskObserver.observe({ entryTypes: ['longtask'] });
  } catch (_) {
    // longtask not supported in this Chromium build
  }
}

export async function exportBattlePerfReport(extra = {}) {
  const probe = getBattlePerfProbe();
  if (!probe) {
    throw new Error('Battle perf probe is not enabled');
  }
  const report = probe.buildExportReport(extra);

  if (window.electron?.perf?.exportReport) {
    const result = await window.electron.perf.exportReport(report);
    console.log('[BattlePerf] Report written to:', result?.filepath);
    if (Array.isArray(report.writtenSummary)) {
      console.log('[BattlePerf] Summary:\n' + report.writtenSummary.map((line) => `  • ${line}`).join('\n'));
    }
    return { report, ...result };
  }

  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `battle-perf-${Date.now()}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
  return { report, filepath: anchor.download };
}

export function installBattlePerfGlobalExport() {
  if (typeof window === 'undefined' || !isBattlePerfEnabled()) {
    return;
  }
  window.__codaExportBattlePerf = exportBattlePerfReport;
  window.__codaBattlePerfSnapshot = () => getBattlePerfProbe()?.getSnapshot() ?? null;
}
